import { useState, useEffect } from "react";
import axios from "axios";
import { API, APP_LOGO_POWERED, APP_LOGO_WHITE, Icon, useAuth } from "../lib/core";

export const InstallHelpModal = ({ onClose }) => {
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="install-help-modal">
      <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-center space-y-3" onClick={(e) => e.stopPropagation()}>
        <img src="/topit-app-192.png" alt="TopIt" className="w-16 h-16 rounded-2xl mx-auto shadow" />
        <h3 className="font-bold text-lg text-slate-900">Install TopIt</h3>
        {isIOS ? (
          <p className="text-sm text-slate-500">
            Tap the <span className="font-semibold text-slate-700">Share</span> icon
            <span className="inline-flex items-center px-1"><Icon name="ios_share" className="text-base text-primary-custom" /></span>
            in Safari, then choose <span className="font-semibold text-slate-700">“Add to Home Screen”</span>.
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            Open your browser menu <span className="font-semibold text-slate-700">(⋮)</span> and tap
            <span className="font-semibold text-slate-700"> “Add to Home screen”</span> / <span className="font-semibold text-slate-700">“Install app”</span> to add TopIt as a shortcut.
          </p>
        )}
        <button onClick={onClose} data-testid="install-help-close" className="mt-2 w-full h-11 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold transition-all">Got it</button>
      </div>
    </div>
  );
};

// "Add to Home Screen" install icon — taps the native PWA prompt (Android/desktop)
// or shows manual instructions (iOS Safari). Hidden when already installed.

export const InstallAppButton = ({ className = "" }) => {
  const [deferred, setDeferred] = useState(typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }
    const onAvail = () => setDeferred(window.__deferredInstallPrompt || null);
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("tt-install-available", onAvail);
    window.addEventListener("tt-app-installed", onInstalled);
    return () => {
      window.removeEventListener("tt-install-available", onAvail);
      window.removeEventListener("tt-app-installed", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { /* dismissed */ }
      window.__deferredInstallPrompt = null;
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  if (installed) return null;

  return (
    <div className={`flex flex-col items-center ${className}`} data-testid="install-app">
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-icon"
        aria-label="Add TopIt to your home screen"
        className="group relative transition-transform duration-200 hover:-translate-y-0.5 active:scale-95"
      >
        <img
          src="/topit-app-192.png"
          alt="Add TopIt to Home Screen"
          className="w-16 h-16 rounded-2xl shadow-lg ring-1 ring-black/5"
        />
        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary-custom text-white flex items-center justify-center shadow-md">
          <Icon name="add" className="text-base" />
        </span>
      </button>
      <span className="mt-2 text-xs font-medium text-slate-500">Add to Home Screen</span>

      {showHelp && <InstallHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

// Subtle dismissible banner shown to logged-in users to boost installs

export const InstallBanner = () => {
  const [deferred, setDeferred] = useState(typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null);
  const [showHelp, setShowHelp] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const dismissed = localStorage.getItem("topit_install_dismissed") === "1";
    if (standalone || dismissed) { setHidden(true); return; }
    const onAvail = () => setDeferred(window.__deferredInstallPrompt || null);
    const onInstalled = () => setHidden(true);
    window.addEventListener("tt-install-available", onAvail);
    window.addEventListener("tt-app-installed", onInstalled);
    return () => {
      window.removeEventListener("tt-install-available", onAvail);
      window.removeEventListener("tt-app-installed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { /* dismissed */ }
      window.__deferredInstallPrompt = null;
      setDeferred(null);
      setHidden(true);
    } else {
      setShowHelp(true);
    }
  };

  const dismiss = () => { localStorage.setItem("topit_install_dismissed", "1"); setHidden(true); };

  if (hidden) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 border border-white/10 p-4 flex items-center gap-4" data-testid="install-banner">
      <img src="/topit-app-192.png" alt="TopIt" className="w-12 h-12 rounded-xl shadow-md shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white text-sm">Install the TopIt app</p>
        <p className="text-slate-300 text-xs">Add to your home screen for one-tap, faster top-ups.</p>
      </div>
      <button onClick={handleInstall} data-testid="install-banner-btn" className="shrink-0 bg-primary-custom hover:bg-primary-dark text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-1.5">
        <Icon name="download" className="text-base" /> Install
      </button>
      <button onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Dismiss" className="shrink-0 text-slate-400 hover:text-white p-1">
        <Icon name="close" className="text-lg" />
      </button>
      {showHelp && <InstallHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

// Icon component using Material Symbols

export const LoginPage = ({ onSwitchToSignup, onForgotPassword, onNeedVerification }) => {
  const { login } = useAuth();
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem("topit_session_expired") === "1") {
        setSessionExpired(true);
        localStorage.removeItem("topit_session_expired");
      }
    } catch { /* noop */ }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/user/login`, {
        email_or_phone: emailOrPhone,
        password: password,
      });
      login(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        // Send a fresh code and route to the verification screen
        try { await axios.post(`${API}/user/resend-verification`, { email_or_phone: detail.email || emailOrPhone }); } catch { /* cooldown ok */ }
        onNeedVerification(detail.email || emailOrPhone);
        return;
      }
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-sidebar-dark relative overflow-hidden">
      {/* Subtle red gradient accents */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-custom/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-custom/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md animate-fadeIn">
        {/* Logo on dark bg */}
        <div className="flex flex-col items-center mb-6">
          <div className="flex flex-col items-center" data-testid="topit-logo">
            <img src={APP_LOGO_WHITE} alt="TopIt" className="h-11 w-auto object-contain drop-shadow-lg" />
            <span className="text-slate-400 text-[10px] tracking-wide mt-1.5 leading-none" data-testid="powered-by-sineli">Powered by Sineli</span>
          </div>
          <p className="text-slate-400 text-sm mt-3">Instant Top-Ups, Anytime, Anywhere.</p>
        </div>

        {/* White card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-7">
            <h1 className="text-slate-900 text-2xl font-bold tracking-tight">
              Welcome back <span className="inline-block">👋</span>
            </h1>
            <p className="text-slate-500 text-sm mt-1.5">Sign in to continue to your account</p>
          </div>

          {sessionExpired && !error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl flex items-center gap-2 mb-4" data-testid="login-session-expired">
              <Icon name="schedule" className="text-amber-500" />
              <span className="text-sm">Your session expired — please sign in again.</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-4" data-testid="login-error">
              <Icon name="error" className="text-red-500" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Email or Phone Number</label>
              <div className="relative flex items-center">
                <Icon name="person" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="text"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base font-normal placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your email or phone"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-11 text-base font-normal placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-4 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? "visibility_off" : "visibility"} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-primary-custom focus:ring-primary-custom/30"
                  data-testid="remember-me"
                />
                <span className="text-sm text-slate-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-primary-custom text-sm font-semibold hover:underline cursor-pointer"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold text-base shadow-lg shadow-primary-custom/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
            >
              {loading ? (
                <>
                  <Icon name="progress_activity" className="animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          <div className="text-center pt-5 mt-4 border-t border-slate-100">
            <p className="text-slate-600 text-sm">
              Don't have an account?{" "}
              <button
                onClick={onSwitchToSignup}
                className="text-primary-custom font-bold hover:underline"
                data-testid="switch-to-signup"
              >
                Create account
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Signup Page Component

export const SignupPage = ({ onSwitchToLogin, onNeedVerification }) => {
  const { login } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [partnerCode, setPartnerCode] = useState(() => {
    try { return localStorage.getItem("topit_partner_code") || ""; } catch { return ""; }
  });
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/user/signup`, {
        full_name: fullName,
        email: email,
        phone: phone,
        password: password,
        partner_code: partnerCode ? partnerCode.trim().toUpperCase() : undefined,
        birth_month: birthMonth ? Number(birthMonth) : undefined,
        birth_day: birthDay ? Number(birthDay) : undefined,
      });
      try { if (partnerCode) localStorage.removeItem("topit_partner_code"); } catch { /* noop */ }
      if (response.data?.requires_verification) {
        onNeedVerification(response.data.email || email);
      } else {
        login(response.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-sidebar-dark relative overflow-hidden">
      {/* Subtle red gradient accents */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-custom/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-custom/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md animate-fadeIn">
        {/* Logo on dark bg */}
        <div className="flex flex-col items-center mb-5">
          <div className="flex flex-col items-center">
            <img src={APP_LOGO_WHITE} alt="TopIt" className="h-11 w-auto object-contain drop-shadow-lg" />
            <span className="text-slate-400 text-[10px] tracking-wide mt-1.5 leading-none" data-testid="powered-by-sineli">Powered by Sineli</span>
          </div>
          <p className="text-slate-400 text-sm mt-3">Instant Top-Ups, Anytime, Anywhere.</p>
        </div>

        {/* White card */}
        <div className="bg-white rounded-3xl p-7 shadow-2xl">
          <div className="text-center mb-6">
            <h1 className="text-slate-900 text-2xl font-bold tracking-tight">Create your account</h1>
            <p className="text-slate-500 text-sm mt-1.5">Sign up to start topping up instantly</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-4" data-testid="signup-error">
              <Icon name="error" className="text-red-500" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSignup} className="space-y-3.5">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Full Name</label>
              <div className="relative flex items-center">
                <Icon name="badge" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Email Address</label>
              <div className="relative flex items-center">
                <Icon name="mail" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Phone Number</label>
              <div className="relative flex items-center">
                <Icon name="phone" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your phone number"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Birthday <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative flex items-center">
                  <Icon name="cake" className="absolute left-4 text-slate-400 z-10" />
                  <select
                    data-testid="signup-birth-month"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    className="appearance-none flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base text-slate-700 outline-none transition-all"
                  >
                    <option value="">Month</option>
                    {["January","February","March","April","May","June","July","August","September","October","November","December"].map((mo, i) => (
                      <option key={i} value={i + 1}>{mo}</option>
                    ))}
                  </select>
                </div>
                <div className="relative flex items-center">
                  <select
                    data-testid="signup-birth-day"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    className="appearance-none flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 px-4 text-base text-slate-700 outline-none transition-all"
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-slate-400 text-xs" data-testid="signup-birthday-consent">🎂 We'll use this to send you birthday treats — airtime, data, cashback or special discounts. You can leave it blank.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-11 text-base placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? "visibility_off" : "visibility"} />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Confirm Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Confirm your password"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Partner / Referral Code <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="relative flex items-center">
                <Icon name="handshake" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="signup-partner-code"
                  type="text"
                  value={partnerCode}
                  onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                  className="flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 pl-11 pr-4 text-base placeholder:text-slate-400 outline-none transition-all uppercase"
                  placeholder="Enter a partner code"
                />
              </div>
            </div>

            <button
              data-testid="signup-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold text-base shadow-lg shadow-primary-custom/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
            >
              {loading ? (
                <>
                  <Icon name="progress_activity" className="animate-spin" />
                  <span>Creating account...</span>
                </>
              ) : (
                <span>Create account</span>
              )}
            </button>
          </form>

          <div className="text-center pt-5 mt-4 border-t border-slate-100">
            <p className="text-slate-600 text-sm">
              Already have an account?{" "}
              <button
                onClick={onSwitchToLogin}
                className="text-primary-custom font-bold hover:underline"
                data-testid="switch-to-login"
              >
                Sign in
              </button>
            </p>
            <InstallAppButton className="mt-6" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Landing / Onboarding Page Component

export const LANDING_HERO = "https://static.prod-images.emergentagent.com/jobs/0758cada-57de-497b-bfee-1e90ab880735/images/861758477a30c2b9d94abd90036789f554004c4016957850f47d1e728aa8108d.png";


export const LANDING_SLIDES = [
  {
    eyebrow: ["TOP UP.", "SAVE MORE.", "DO MORE."],
    title: "Top up airtime and data instantly",
    highlight: "instantly",
    subtitle: "Enjoy cheaper rates, instant delivery and seamless transactions.",
  },
  {
    eyebrow: ["PAY LESS.", "EARN MORE.", "EVERY DAY."],
    title: "Get 5% cashback on every data purchase",
    highlight: "5% cashback",
    subtitle: "Plus 2% cashback on every recharge from your wallet. The more you top up, the more you save.",
  },
  {
    eyebrow: ["SET IT.", "FORGET IT.", "DONE."],
    title: "Schedule recharges, never forget",
    highlight: "forget",
    subtitle: "Save your beneficiaries and let TopIt auto-recharge them at the right time — every time.",
  },
];

// Resolve a slide image to an absolute URL (falls back to the shared hero).
const resolveSlideImage = (img) => {
  if (!img) return LANDING_HERO;
  if (/^https?:\/\//.test(img) || img.startsWith("data:")) return img;
  return `${API}${img.replace(/^\/api/, "")}`;
};

// Render a title string with the highlighted portion in the brand red.
const renderSlideTitle = (title, highlight, slideKey) => {
  const t = title || "";
  const h = (highlight || "").trim();
  const idx = h ? t.toLowerCase().indexOf(h.toLowerCase()) : -1;
  if (idx === -1) return <span>{t}</span>;
  return (
    <>
      {idx > 0 && <span>{t.slice(0, idx)}</span>}
      <span className="text-primary-custom">{t.slice(idx, idx + h.length)}</span>
      {idx + h.length < t.length && <span>{t.slice(idx + h.length)}</span>}
    </>
  );
};


export const LandingPage = ({ onGetStarted, onLogin }) => {
  const [slide, setSlide] = useState(0);
  const [slides, setSlides] = useState(LANDING_SLIDES);
  const total = slides.length;
  const current = slides[slide] || slides[0];

  // Load admin-managed onboarding slides (falls back to built-in defaults).
  useEffect(() => {
    let active = true;
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((d) => {
        if (active && Array.isArray(d?.landing_slides) && d.landing_slides.length) {
          setSlides(d.landing_slides);
          setSlide(0);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Auto-advance every 6s
  useEffect(() => {
    const t = setTimeout(() => setSlide((s) => (s + 1) % total), 6000);
    return () => clearTimeout(t);
  }, [slide, total]);

  return (
    <div className="min-h-screen w-full bg-white relative overflow-hidden" data-testid="landing-page">
      {/* Decorative swirls top-left */}
      <svg className="absolute -top-12 -left-8 w-72 h-72 pointer-events-none opacity-50" viewBox="0 0 200 200" fill="none">
        <path d="M-20 80 Q 40 20, 120 60 T 240 80" stroke="#FCA5A5" strokeWidth="1.5" fill="none" />
        <path d="M-20 100 Q 50 40, 130 80 T 250 100" stroke="#FCA5A5" strokeWidth="1.5" fill="none" />
        <path d="M-20 120 Q 60 60, 140 100 T 260 120" stroke="#FECACA" strokeWidth="1.5" fill="none" />
      </svg>

      {/* Decorative red wave bottom-right */}
      <svg className="absolute bottom-0 right-0 w-80 h-64 pointer-events-none" viewBox="0 0 320 240" fill="none" preserveAspectRatio="none">
        <path d="M50 240 Q 120 140, 320 180 L 320 240 Z" fill="#E11D2E" opacity="0.85" />
        <path d="M150 240 Q 220 200, 320 220 L 320 240 Z" fill="#B91C1C" opacity="0.6" />
      </svg>

      {/* Skip button top right */}
      <button
        type="button"
        data-testid="landing-skip"
        onClick={onLogin}
        className="absolute top-6 right-5 z-20 px-5 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold bg-white/80 backdrop-blur hover:bg-slate-50 transition-all"
      >
        Skip
      </button>

      {/* Content container */}
      <div className="relative z-10 max-w-md mx-auto min-h-screen flex flex-col px-6 pt-16 pb-8">
        {/* Brand wordmark */}
        <div className="flex flex-col items-center text-center">
          <img src={APP_LOGO_POWERED} alt="TopIt — Powered by Sineli" className="h-28 md:h-36 object-contain drop-shadow-sm" data-testid="landing-logo" fetchPriority="high" decoding="async" />

          {/* Tagline TOP UP. SAVE MORE. DO MORE. */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 tracking-[0.15em] text-xs sm:text-sm font-bold">
            {(current.eyebrow || []).map((word, i) => (
              <span key={`${slide}-eb-${i}`} className={i === 1 ? "text-primary-custom" : "text-slate-900"}>
                {word}
              </span>
            ))}
          </div>
        </div>

        {/* Heading + subtitle */}
        <div className="text-center mt-6 px-2">
          <h1 className="text-slate-900 text-3xl sm:text-4xl font-bold leading-tight tracking-tight" data-testid="landing-slide-title">
            {renderSlideTitle(current.title, current.highlight, slide)}
          </h1>
          <p className="text-slate-500 mt-3 leading-relaxed text-sm sm:text-base">{current.subtitle}</p>
        </div>

        {/* Hero illustration */}
        <div className="flex-1 flex items-center justify-center my-4 min-h-[280px]">
          <img
            src={resolveSlideImage(current.image)}
            alt=""
            className="w-full max-w-[300px] object-contain drop-shadow-2xl"
            style={{ animation: "fadeIn 0.6s ease" }}
            key={slide}
            fetchPriority="high"
            decoding="async"
          />
        </div>

        {/* Social proof row */}
        <div className="flex items-center justify-center gap-4 mb-4 text-xs sm:text-sm" data-testid="landing-social-proof">
          <div className="flex items-center gap-1.5 text-slate-700">
            <Icon name="verified" className="text-primary-custom text-base" />
            <span className="font-semibold">Trusted by 50,000+ Nigerians</span>
          </div>
          <span className="text-slate-300">·</span>
          <div className="flex items-center gap-1 text-slate-700">
            <span className="text-yellow-400 text-base leading-none">★</span>
            <span className="font-semibold">4.8 rating</span>
          </div>
        </div>

        {/* Slide indicator dots */}
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="landing-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`landing-dot-${i}`}
              onClick={() => setSlide(i)}
              className={`transition-all rounded-full ${
                i === slide ? "w-7 h-2 bg-primary-custom" : "w-2 h-2 bg-slate-300 hover:bg-slate-400"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Get Started button */}
        <button
          type="button"
          data-testid="landing-get-started"
          onClick={onGetStarted}
          className="w-full h-14 bg-primary-custom hover:bg-primary-dark text-white rounded-2xl font-bold text-base shadow-xl shadow-primary-custom/30 transition-all flex items-center justify-center gap-2"
        >
          <span>Get Started</span>
          <Icon name="chevron_right" className="text-xl" />
        </button>

        {/* Log in link */}
        <p className="text-center text-slate-600 text-sm mt-5">
          Already have an account?{" "}
          <button
            type="button"
            data-testid="landing-login-link"
            onClick={onLogin}
            className="text-primary-custom font-bold hover:underline"
          >
            Log in
          </button>
        </p>
        <InstallAppButton className="mt-7" />
      </div>
    </div>
  );
};


// Forgot Password Page Component

export const VerifyEmailPage = ({ email, onBack }) => {
  const { login } = useAuth();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/user/verify-email`, { email_or_phone: email, otp });
      login(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError("");
    setInfo("");
    try {
      await axios.post(`${API}/user/resend-verification`, { email_or_phone: email });
      setInfo("A new code has been sent to your email.");
      setCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const m = typeof detail === "string" ? detail : "Please wait before requesting another code.";
      setError(m);
      const match = /(\d+)s/.exec(m);
      if (match) setCooldown(parseInt(match[1], 10));
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-sidebar-dark relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-custom/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-custom/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md animate-fadeIn">
        <div className="flex flex-col items-center mb-6">
          <div className="flex flex-col items-center">
            <img src={APP_LOGO_WHITE} alt="TopIt" className="h-11 w-auto object-contain drop-shadow-lg" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-7 shadow-2xl" data-testid="verify-email-card">
          <div className="w-14 h-14 rounded-full bg-red-50 text-primary-custom flex items-center justify-center mx-auto mb-4">
            <Icon name="mark_email_read" className="text-3xl" />
          </div>
          <div className="text-center mb-6">
            <h1 className="text-slate-900 text-2xl font-bold tracking-tight">Verify your email</h1>
            <p className="text-slate-500 mt-2 text-sm">
              Enter the 6-digit code we sent to{" "}
              <span className="font-semibold text-slate-700" data-testid="verify-email-address">{email}</span>.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-4" data-testid="verify-email-error">
              <Icon name="error" className="text-red-500" /><span>{error}</span>
            </div>
          )}
          {info && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-4" data-testid="verify-email-info">
              <Icon name="check_circle" className="text-green-500" /><span>{info}</span>
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <input
              data-testid="verify-email-otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="w-full rounded-xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 px-4 text-center text-2xl font-bold tracking-[0.5em] outline-none transition-all"
              placeholder="------"
              required
            />
            <button
              data-testid="verify-email-submit"
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (<><Icon name="progress_activity" className="animate-spin" /><span>Verifying...</span></>) : (<><span>Verify & Continue</span><Icon name="arrow_forward" /></>)}
            </button>
          </form>

          <div className="text-center mt-5 text-sm text-slate-500">
            Didn't get it?{" "}
            <button
              data-testid="verify-email-resend"
              onClick={handleResend}
              disabled={cooldown > 0}
              className="text-primary-custom font-semibold disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
          <div className="text-center mt-2">
            <button data-testid="verify-email-back" onClick={onBack} className="text-slate-400 text-sm hover:text-slate-600">Back to login</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Enter email/phone, 2: Enter OTP, 3: New password
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState(""); // For testing display

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/user/forgot-password`, {
        email_or_phone: emailOrPhone,
      });
      setGeneratedOtp(response.data.otp); // Store OTP for testing display
      setSuccess(`OTP sent! Check your email/phone.`);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/user/verify-otp`, {
        email_or_phone: emailOrPhone,
        otp: otp,
      });
      setSuccess("OTP verified! Set your new password.");
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/user/reset-password`, {
        email_or_phone: emailOrPhone,
        otp: otp,
        new_password: newPassword,
      });
      setSuccess("Password reset successfully! You can now log in.");
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center p-4 bg-background-light">
      <div className="w-full max-w-[480px] space-y-6 animate-fadeIn">
        {/* Logo Section */}
        <div className="bg-cover bg-center flex flex-col justify-end overflow-hidden rounded-xl min-h-48 relative shadow-lg"
          style={{
            backgroundImage: `linear-gradient(0deg, rgba(153, 27, 27, 0.8) 0%, rgba(153, 27, 27, 0.2) 100%), url("https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop&q=60")`
          }}>
          <div className="flex p-6">
            <img 
              src="/topit-logo-powered-box.png" 
              alt="TopIt — Powered by Sineli" 
              className="h-20 w-auto object-contain rounded-lg bg-white p-2"
              data-testid="powered-by-sineli"
            />
          </div>
        </div>

        {/* Header Content */}
        <div className="text-center">
          <h1 className="text-slate-900 tracking-tight text-[28px] font-bold leading-tight">
            {step === 1 && "Forgot Password"}
            {step === 2 && "Enter OTP"}
            {step === 3 && "Reset Password"}
          </h1>
          <p className="text-slate-500 mt-2">
            {step === 1 && "Enter your email or phone to receive a reset code."}
            {step === 2 && "Enter the 6-digit code sent to your email/phone."}
            {step === 3 && "Create your new password."}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-10 h-1.5 rounded-full transition-all ${
                s <= step ? "bg-primary-custom" : "bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <Icon name="error" className="text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <Icon name="check_circle" className="text-green-500" />
            <span>{success}</span>
          </div>
        )}

        {/* Testing OTP Display */}
        {generatedOtp && step === 2 && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-center">
            <p className="text-sm">For testing: Your OTP is</p>
            <p className="font-bold text-2xl tracking-wider">{generatedOtp}</p>
          </div>
        )}

        {/* Step 1: Enter Email/Phone */}
        {step === 1 && (
          <form onSubmit={handleRequestOTP} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Email or Phone Number</label>
              <div className="relative flex items-center">
                <Icon name="person" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="forgot-email-input"
                  type="text"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base font-normal placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter your email or phone"
                  required
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                data-testid="request-otp-btn"
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send OTP</span>
                    <Icon name="send" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Enter OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Enter 6-digit OTP</label>
              <div className="relative flex items-center">
                <Icon name="pin" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="otp-input"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="flex w-full rounded-xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-2xl font-bold tracking-[0.5em] text-center placeholder:text-slate-400 outline-none transition-all"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <button
                data-testid="verify-otp-btn"
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <span>Verify OTP</span>
                    <Icon name="check_circle" />
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setOtp("");
                  setError("");
                  setSuccess("");
                }}
                className="w-full h-12 text-primary-custom hover:bg-primary-custom/5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Icon name="refresh" />
                <span>Resend OTP</span>
              </button>
            </div>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">New Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="new-password-input"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-12 text-base font-normal placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 text-slate-400 hover:text-primary-custom transition-colors"
                >
                  <Icon name={showPassword ? "visibility_off" : "visibility"} />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Confirm New Password</label>
              <div className="relative flex items-center">
                <Icon name="lock" className="absolute left-4 text-slate-400" />
                <input
                  data-testid="confirm-password-input"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="flex w-full rounded-xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-14 pl-12 pr-4 text-base font-normal placeholder:text-slate-400 outline-none transition-all"
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                data-testid="reset-password-btn"
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    <span>Resetting...</span>
                  </>
                ) : (
                  <>
                    <span>Reset Password</span>
                    <Icon name="key" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Back to Login */}
        <div className="text-center pt-2">
          <button
            onClick={onBack}
            className="text-slate-600 hover:text-primary-custom flex items-center justify-center gap-2 mx-auto"
          >
            <Icon name="arrow_back" />
            <span>Back to Login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Profile Page Component
